import React, { useCallback, useEffect } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DevTool } from '@hookform/devtools';
import { LoaderFunction, NavLink, useFetcher, useNavigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import MainSection from '../../../../components/Core/MainSection/MainSection';
import Card from '../../../../components/Core/Card/Card';
import TextFieldControlled, {
  TextAreaFieldControlled,
} from '../../../../components/Form/ControlledFields/TextFieldControlled';
import Label from '../../../../components/Core/Label/Label';
import { useMainTitle } from '../../../../components/Layout/MainTitleContext';
import DropzoneFileInput from '../../../../components/Core/DropzoneFileInput/DropzoneFileInput';
import ConnectForm from '../../../../components/Form/ConnectForm';
import ButtonWithIcon from '../../../../components/Core/Button/ButtonWithIcon';
import useDisplayError from '../../../../hook/useDisplayError';
import { TOAST_SEVERITY } from '../../../../components/Core/Toast/Toast';
import { useSnackbar } from '../../../../context/SnackbarContext';
import store from '../../../../internal/store';
import styles from './NewBike.module.scss';

export const newFreeParkLoader: (queryClient: QueryClient) => LoaderFunction = () => async () => {
  const collId = store.getState().authState.collectivity;

  return { collId };
};

export function FreeParkForm() {
  const { t } = useTranslation(['parameters/products/newFreePark', 'form']);
  const fieldsT = useCallback((key: string) => t(`fields.${key}`), [t]);

  return (
    <ConnectForm>
      {({ register }) => (
        <>
          <div className={styles.FirstSection}>
            <div className={styles.Left}>
              <fieldset>
                <Label required={true}>{fieldsT('name.label')}</Label>
                <TextFieldControlled
                  placeholder={fieldsT('name.placeholder')}
                  {...register('name', { required: true })}
                />
              </fieldset>
              <fieldset className={styles.DescriptionField}>
                <Label required={true}>{fieldsT('description.label')}</Label>
                <TextAreaFieldControlled
                  placeholder={fieldsT('description.placeholder')}
                  {...register('description', { required: true })}
                />
              </fieldset>
            </div>
            <div className={styles.Right}>
              <fieldset>
                <Controller
                  name={'img'}
                  defaultValue={null}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Label>{t('fields.img.label')}</Label>
                      <DropzoneFileInput
                        returnFile
                        className={styles.ImgInput}
                        //eslint-disable-next-line
                        onChange={(file: any) => {
                          const reader = new FileReader();
                          reader.onload = function (e) {
                            if (e.target && e.target.result && typeof e.target.result === 'string') {
                              const src = e.target.result;
                              // onChange(dataURLtoFile(src, file.path));
                              onChange({ src });
                            }
                          };

                          reader.readAsDataURL(file as File);
                        }}
                        defaultValue={value}
                      />
                    </>
                  )}
                />
              </fieldset>
            </div>
          </div>
        </>
      )}
    </ConnectForm>
  );
}

function NewFreeParkPage() {
  const fetcher = useFetcher();
  const { t } = useTranslation(['parameters/products/newFreePark', 'form']);
  const methods = useForm({ mode: 'onChange' });
  const navigate = useNavigate();
  useDisplayError(fetcher.data);
  useMainTitle(t('title'));
  const snackbar = useSnackbar();

  const onSubmit = (data: FieldValues) => {
    fetcher.submit({ action: 'new', ...data }, { method: 'post', encType: 'application/json' });
  };

  useEffect(() => {
    if (fetcher.data && fetcher.data.status === 200) {
      snackbar?.setAlert({
        message: t('successMessage'),
        severity: TOAST_SEVERITY.success,
      });
      navigate('/parametres/articles/consigne');
    }
  }, [fetcher.data, t]);

  return (
    <MainSection className={'reset'}>
      <Card
        content={
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <p>{t('form:all_required')}</p>
              <FreeParkForm />
              <div className={'row mt-4 justify-between items-center'}>
                <ButtonWithIcon
                  as={NavLink}
                  to={'/parametres/articles/consigne-libre'}
                  icon={'CrossWithoutCircle'}
                  color={'secondary'}
                  type={'button'}>
                  {t('common:cancel')}
                </ButtonWithIcon>
                <ButtonWithIcon icon={'CheckWithoutCircle'} type={'submit'}>
                  {t('submitCta')}
                </ButtonWithIcon>
              </div>
            </form>

            <DevTool control={methods.control} />
          </FormProvider>
        }
      />
    </MainSection>
  );
}

export default NewFreeParkPage;
